'use client';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useSensors, useSensor, KeyboardSensor, DndContext, closestCenter } from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import { convertCssNumberToString } from '../../helpers/convertCssNumberToString.js';
import { withTitan } from '../../hocs/withTitan/withTitan.js';
import { defaultStyles } from '../../mixins/defaultStyles.js';
import { scrollbarStyle } from '../../mixins/scrollbarStyle.js';
import { spaceProp } from '../../mixins/spaceProp.js';
import { textStyle } from '../../mixins/textStyle.js';
import { TableFooter } from './TableFooter.js';
import { SmartPointerSensor, pointerSensorOptions } from './helpers/dnd-kit.js';
import { TableBody } from './TableBody.js';
import { TableEmptyState } from './TableEmptyState.js';
import { TableHead } from './TableHead.js';
import { useTable } from './TableProvider.js';

const TableContainer = styled.div.withConfig({
  componentId: "sc-18x77wf-0"
})(["overflow-x:auto;", ";"], scrollbarStyle.componentScrollbar);
const TableWidthContainer = styled.div.withConfig({
  componentId: "sc-18x77wf-1"
})(["", " ", ""], ({
  width
}) => width ? `width: ${convertCssNumberToString(width)};` : 'max-width: max-content;', ({
  spaceAfter
}) => spaceProp(spaceAfter));
const StyledTable = styled.table.withConfig({
  shouldForwardProp: (prop, defaultValidatorFn) => !['width'].includes(prop) && defaultValidatorFn(prop)
}).withConfig({
  componentId: "sc-18x77wf-2"
})(["", " ", ";", ";border-collapse:collapse;position:relative;margin:0;padding:0;width:100%;width:max-content;", ""], textStyle('paragraph'), defaultStyles.fontSmoothing, defaultStyles.boxSizing, ({
  width
}) => width && `width: ${convertCssNumberToString(width)};`);
const WithDnDContext = ({
  children
}) => {
  const {
    isDraggable,
    handleDragEnd,
    onRowClick
  } = useTable();
  const sensors = useSensors(useSensor(SmartPointerSensor, pointerSensorOptions(!!onRowClick)), useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates
  }));
  if (!isDraggable) return jsx(Fragment, {
    children: children
  });
  return jsx(DndContext, {
    collisionDetection: closestCenter,
    modifiers: [restrictToVerticalAxis, restrictToFirstScrollableAncestor],
    sensors: sensors,
    onDragEnd: handleDragEnd,
    children: children
  });
};
const Table = /*#__PURE__*/withTitan(({
  emptyStateProps,
  spaceAfter,
  width,
  testId = 'table'
}) => {
  const {
    instance,
    isLoading
  } = useTable();
  const hasData = instance.getRowModel().rows.length > 0;
  if (!isLoading && !hasData && emptyStateProps) {
    return jsx(TableEmptyState, Object.assign({}, emptyStateProps));
  }
  return jsx(WithDnDContext, {
    children: jsxs(TableWidthContainer, {
      spaceAfter: spaceAfter,
      width: width,
      children: [jsx(TableContainer, {
        "data-test-id": testId,
        children: jsx(AnimatePresence, {
          initial: false,
          children: jsxs(StyledTable, {
            width: width,
            children: [jsx(TableHead, {}), jsx(TableBody, {})]
          })
        })
      }), jsx(TableFooter, {})]
    })
  });
}, {
  name: 'Table'
});

export { Table };
//# sourceMappingURL=Table.js.map
